import { useEffect, useSyncExternalStore } from 'react';
import { messagesService } from '@/services/messages';

/** Evento local que dispara el hilo al marcar leído — el badge se refresca al
 *  instante sin esperar el próximo sondeo. */
export const MESSAGES_READ_EVENT = 'vaultex:messages-read';

// 2-nov (react-best-practices, client-swr-dedup): UNA sola fuente compartida.
// El menú lateral y la barra móvil montan el hook a la vez (la barra solo se
// oculta por CSS); antes cada uno abría su canal realtime, su sondeo de 60 s y
// sus listeners. Ahora el primero que se monta enciende la fuente y el último
// que se desmonta la apaga.
let count = 0;
let users = 0;
let stop: (() => void) | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function start(): () => void {
  let alive = true;
  const refresh = () => void messagesService.unread().then((n) => {
    if (alive && n !== count) { count = n; emit(); }
  });
  refresh();
  const unsub = messagesService.subscribe(refresh);
  const iv = setInterval(refresh, 60000);
  window.addEventListener('focus', refresh);
  window.addEventListener(MESSAGES_READ_EVENT, refresh);
  return () => {
    alive = false;
    unsub();
    clearInterval(iv);
    window.removeEventListener('focus', refresh);
    window.removeEventListener(MESSAGES_READ_EVENT, refresh);
  };
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** No-leídos que me esperan (VLT-214 fase 2): REALTIME — cualquier mensaje
 *  nuevo visible bajo mi RLS refresca el badge al instante; el sondeo de 60 s
 *  queda como respaldo, más el refresco al volver el foco y al marcar leído. */
export function useUnreadMessages(enabled: boolean): number {
  useEffect(() => {
    if (!enabled) return;
    users += 1;
    if (users === 1) stop = start();
    return () => {
      users -= 1;
      if (users === 0) {
        stop?.();
        stop = null;
        count = 0;
      }
    };
  }, [enabled]);
  const n = useSyncExternalStore(subscribe, () => count, () => 0);
  return enabled ? n : 0;
}
